import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowDown, Loader2, Check, BookOpen } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { Reveal, Eyebrow } from "@/components/bits";
import { setMetaTags } from "@/seo";

const STRIPE_BOOK_URL = "https://buy.stripe.com/eVqfZj2nKbOr9KU7gFeME04";
// January 25, 2027, 00:00 WAT (UTC+1)
const RELEASE_AT = new Date("2027-01-25T00:00:00+01:00").getTime();

const inputCls =
  "w-full bg-white border border-tnn-ink/12 rounded-xl px-4 py-3 text-sm text-tnn-ink placeholder:text-tnn-muted/60 focus:outline-none focus:border-tnn-purple focus:ring-2 focus:ring-tnn-purple/15 transition-[border-color,box-shadow] duration-200";
const labelCls = "block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-purple mb-2";

const FOUR_V = [
  {
    title: "Patient Value",
    question: "Is it useful and usable?",
    body: "Does the patient understand it, trust it, use it and benefit from it?",
    accent: "#F05A24",
  },
  {
    title: "Clinical Value",
    question: "Is it safe and clinically meaningful?",
    body: "Does it support appropriate clinical decision-making and better care?",
    accent: "#E8A020",
  },
  {
    title: "System Value",
    question: "Does it fit the healthcare system?",
    body: "Can it work within real healthcare workflows, infrastructure and constraints?",
    accent: "#1F8A4C",
  },
  {
    title: "Business Value",
    question: "Can it survive and scale?",
    body: "Who pays? Who operates it? What makes it sustainable?",
    accent: "#F2715C",
  },
];

const AUDIENCES = [
  { title: "Healthcare Founders", body: "For founders building technology for real healthcare environments." },
  { title: "Product Managers & Designers", body: "For people translating complex healthcare problems into products." },
  { title: "Clinicians", body: "For clinicians who want to understand how healthcare products are designed and built." },
  { title: "Healthcare Leaders & Innovators", body: "For people evaluating digital health, AI and healthcare technology." },
];

const BOOK_INFO = [
  ["Author", "Tito Ipinmoye"],
  ["Release date", "January 25, 2027"],
  ["Paperback preorder", "$24.99"],
  ["Regular paperback price", "$34.99"],
  ["Kindle edition", "$9.99, available at launch"],
];

function scrollToWaitlist() {
  document.getElementById("book-waitlist")?.scrollIntoView({ behavior: "smooth", block: "start" });
}

function PreorderBlock({ testidPrefix }) {
  const launched = Date.now() >= RELEASE_AT;
  return (
    <div className="max-w-xl">
      <div className="flex flex-col sm:flex-row gap-3">
        <a
          href={STRIPE_BOOK_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testidPrefix}-preorder-button`}
          className="group inline-flex items-center justify-center gap-2 whitespace-nowrap bg-tnn-purple text-white font-semibold text-[13px] sm:text-sm px-7 py-4 rounded-full hover:bg-tnn-deep shadow-sm transition-colors duration-200"
        >
          {launched ? "BUY THE PAPERBACK, $34.99" : "PRE-ORDER THE PAPERBACK, $24.99"}
          <ArrowUpRight className="w-4 h-4 shrink-0 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
        </a>
        <button
          onClick={scrollToWaitlist}
          data-testid={`${testidPrefix}-waitlist-button`}
          className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-white border border-tnn-ink/15 text-tnn-ink font-semibold text-[13px] sm:text-sm px-7 py-4 rounded-full shadow-sm hover:border-tnn-purple hover:text-tnn-purple transition-colors duration-200"
        >
          JOIN THE WAITLIST
          <ArrowDown className="w-4 h-4 shrink-0" />
        </button>
      </div>
      <p className="mt-3 text-xs text-tnn-muted leading-relaxed">
        {launched
          ? "Paperback, $34.99. Book price only. Delivery/shipping is not included and will be charged separately based on your delivery location."
          : "Paperback preorder, $24.99 (regular price $34.99 at launch). Book price only. Delivery/shipping is not included and will be charged separately based on your delivery location."}
      </p>
    </div>
  );
}

function LaunchCountdown() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = RELEASE_AT - now;

  if (diff <= 0) {
    return (
      <section className="bg-white border-y border-tnn-ink/8 py-10" data-testid="book-countdown">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="font-display text-2xl sm:text-3xl font-bold text-tnn-ink">THE BOOK IS HERE.</p>
          <p className="mt-2 text-sm text-tnn-muted">Building Digital Health Products That Actually Work is out now.</p>
        </div>
      </section>
    );
  }

  const units = [
    [Math.floor(diff / 86400000), "Days"],
    [Math.floor((diff % 86400000) / 3600000), "Hours"],
    [Math.floor((diff % 3600000) / 60000), "Minutes"],
    [Math.floor((diff % 60000) / 1000), "Seconds"],
  ];

  return (
    <section className="bg-white border-y border-tnn-ink/8 py-10" data-testid="book-countdown">
      <div className="max-w-6xl mx-auto px-6 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-10">
        <div className="text-center sm:text-left">
          <p className="font-display text-xl sm:text-2xl font-bold text-tnn-ink">THE COUNTDOWN IS ON.</p>
          <p className="mt-1 text-xs sm:text-sm text-tnn-muted">
            Building Digital Health Products That Actually Work · January 25, 2027
          </p>
        </div>
        <div className="flex gap-2.5">
          {units.map(([v, label]) => (
            <div key={label} className="bg-tnn-warm border border-tnn-ink/8 rounded-2xl px-3.5 py-2.5 text-center min-w-[64px]">
              <p className="font-display text-xl font-bold text-tnn-purple tabular-nums" data-testid={`book-countdown-${label.toLowerCase()}`}>
                {String(v).padStart(2, "0")}
              </p>
              <p className="mt-0.5 text-[9px] font-bold tracking-[0.16em] uppercase text-tnn-muted">{label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SampleChapter() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle"); // idle | saving | done
  const downloadUrl = `${process.env.REACT_APP_BACKEND_URL}/api/book/sample-chapter`;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setState("saving");
    try {
      await api.post("/book/sample", { email: email.trim() });
      setState("done");
      window.location.assign(downloadUrl);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Please check your email address and try again.");
      setState("idle");
    }
  };

  return (
    <section className="bg-tnn-warm py-16 lg:py-24" data-testid="book-sample">
      <div className="max-w-3xl mx-auto px-6 text-center">
        <Reveal>
          <Eyebrow>Read the First Chapter</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
            Healthcare Is Not Another App
          </h2>
        </Reveal>
        <Reveal delay={0.14}>
          <blockquote className="mt-6 font-display text-xl sm:text-2xl text-tnn-purple leading-snug max-w-xl mx-auto">
            "The first mistake I made building a healthcare product was thinking the product was the app."
          </blockquote>
          <p className="mt-4 text-sm text-tnn-muted leading-relaxed max-w-lg mx-auto">
            A 15-minute introduction to why building healthcare technology is fundamentally different from building
            another digital product.
          </p>
        </Reveal>
        <Reveal delay={0.2}>
          {state === "done" ? (
            <div className="mt-8" data-testid="sample-success">
              <p className="text-sm text-tnn-ink font-semibold">Your download has started.</p>
              <p className="mt-1 text-xs text-tnn-muted">
                Nothing downloaded?{" "}
                <a href={downloadUrl} className="text-tnn-purple font-semibold hover:underline" data-testid="sample-retry-link">
                  Get the chapter directly.
                </a>
              </p>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate className="mt-8 flex flex-col sm:flex-row gap-3 max-w-lg mx-auto" data-testid="sample-form">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                aria-label="Email address"
                data-testid="sample-email-input"
                className={`${inputCls} flex-1`}
              />
              <button
                type="submit"
                disabled={state === "saving"}
                data-testid="sample-download-button"
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap bg-tnn-purple text-white font-semibold text-[13px] sm:text-sm px-7 py-3.5 rounded-full hover:bg-tnn-deep shadow-sm transition-colors duration-200 disabled:opacity-60"
              >
                {state === "saving" ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> PREPARING
                  </>
                ) : (
                  "READ THE SAMPLE CHAPTER"
                )}
              </button>
            </form>
          )}
          <p className="mt-4 text-xs text-tnn-muted">
            Not ready to preorder?{" "}
            <button onClick={scrollToWaitlist} className="text-tnn-purple font-semibold hover:underline" data-testid="sample-waitlist-link">
              Join the waitlist
            </button>
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function WaitlistForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [state, setState] = useState("idle"); // idle | saving | done

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
      toast.error("Please fill in your name, email and phone number.");
      return;
    }
    setState("saving");
    try {
      const { data } = await api.post("/book/waitlist", form);
      setState("done");
      if (data.status === "existing") {
        toast.info("You are already on the list. We will be in touch.");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Please check your details and try again.";
      toast.error(msg);
      setState("idle");
    }
  };

  if (state === "done") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white/10 border border-white/15 rounded-3xl p-10 text-center"
        data-testid="waitlist-success"
      >
        <span className="w-12 h-12 rounded-full bg-tnn-lilac/20 inline-flex items-center justify-center">
          <Check className="w-5 h-5 text-tnn-lilac" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold text-white">You are on the list.</h3>
        <p className="mt-2 text-sm text-white/70 leading-relaxed max-w-sm mx-auto">
          Check your inbox for a welcome note. We will share excerpts, behind-the-scenes updates and the launch
          reminder as January 25, 2027 approaches.
        </p>
      </motion.div>
    );
  }

  return (
    <form onSubmit={onSubmit} className="space-y-5" data-testid="waitlist-form">
      <div>
        <label htmlFor="wl-name" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-lilac mb-2">
          Full Name *
        </label>
        <input
          id="wl-name"
          value={form.name}
          onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
          placeholder="Your full name"
          data-testid="waitlist-name-input"
          className={inputCls}
        />
      </div>
      <div>
        <label htmlFor="wl-email" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-lilac mb-2">
          Email Address *
        </label>
        <input
          id="wl-email"
          type="email"
          value={form.email}
          onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
          placeholder="you@example.com"
          data-testid="waitlist-email-input"
          className={inputCls}
        />
      </div>
      <div>
        <label htmlFor="wl-phone" className="block text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-lilac mb-2">
          Phone Number *
        </label>
        <input
          id="wl-phone"
          type="tel"
          value={form.phone}
          onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
          placeholder="+234 800 000 0000"
          data-testid="waitlist-phone-input"
          className={inputCls}
        />
      </div>
      <button
        type="submit"
        disabled={state === "saving"}
        data-testid="waitlist-submit-button"
        className="w-full inline-flex items-center justify-center gap-2 bg-white text-tnn-deep font-bold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-200 hover:bg-tnn-lilac disabled:opacity-60"
      >
        {state === "saving" ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" /> JOINING
          </>
        ) : (
          "JOIN THE WAITLIST"
        )}
      </button>
    </form>
  );
}

export default function Book() {
  useEffect(() => {
    setMetaTags({
      title: "Building Digital Health Products That Actually Work | TNN Africa",
      description:
        "Building Digital Health Products That Actually Work by Tito Ipinmoye is a practical guide to designing patient-centred, clinically safe and scalable healthcare technology in Africa. Coming January 25, 2027.",
      path: "/book",
      image: "/assets/book-cover.jpg",
    });
  }, []);

  return (
    <main data-testid="book-page">
      {/* Hero */}
      <section className="bg-tnn-warm" data-testid="book-hero">
        <div className="max-w-6xl mx-auto px-6 pt-32 lg:pt-40 pb-16 lg:pb-24 grid lg:grid-cols-2 gap-14 items-center">
          <div>
            <Reveal>
              <Eyebrow>A New Book by Tito Ipinmoye</Eyebrow>
            </Reveal>
            <Reveal delay={0.08}>
              <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-tnn-ink leading-[1.08]">
                Building Digital Health Products That <span className="text-tnn-purple">Actually Work</span>
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-5 text-base sm:text-lg text-tnn-muted leading-relaxed">
                Designing Patient-Centred, Clinically Safe and Scalable Healthcare Technology in Africa
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 font-display text-xl sm:text-2xl text-tnn-ink leading-snug">
                Healthcare doesn't need more technology, it needs technology that actually works.
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <p className="mt-5 inline-flex items-center gap-2 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple bg-tnn-soft px-4 py-2 rounded-full">
                <BookOpen className="w-3.5 h-3.5" /> {Date.now() >= RELEASE_AT ? "Available now" : "Coming January 25, 2027"}
              </p>
            </Reveal>
            <Reveal delay={0.3} className="mt-8">
              <PreorderBlock testidPrefix="book-hero" />
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <div className="relative max-w-sm mx-auto lg:ml-auto w-full">
              <div
                className="absolute -inset-4 rounded-[2rem] bg-tnn-deep/5 rotate-2"
                aria-hidden="true"
              />
              <img
                src="/assets/book-cover.jpg"
                alt="Cover of Building Digital Health Products That Actually Work by Tito Ipinmoye"
                data-testid="book-cover"
                className="relative w-full rounded-lg shadow-[0_28px_60px_rgba(40,0,40,0.35)]"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <LaunchCountdown />

      {/* Intro */}
      <section className="bg-white py-16 lg:py-24" data-testid="book-intro">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Healthcare doesn't need more technology</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <p className="mt-6 font-display text-2xl sm:text-3xl text-tnn-ink leading-snug">
              There is no shortage of health apps, AI tools, dashboards, wearables and digital platforms.
            </p>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-6 text-base sm:text-lg text-tnn-muted leading-relaxed">
              The harder question is whether they actually work: for the patient, for the clinician, for the
              healthcare system and for the business building them.
            </p>
            <p className="mt-4 text-base sm:text-lg text-tnn-muted leading-relaxed">
              Building Digital Health Products That Actually Work explores what happens when clinical expertise
              meets product thinking, technology and the realities of healthcare in Africa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4-V Model */}
      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="book-4v">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>The 4-V Model of Digital Health</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15] max-w-2xl">
              A healthcare product works when four kinds of value align.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {FOUR_V.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.06}>
                <div
                  className="bg-white border border-tnn-ink/6 rounded-3xl p-7 h-full"
                  style={{ borderTop: `3px solid ${v.accent}` }}
                  data-testid={`fourv-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <p className="text-xs font-bold tracking-[0.2em] uppercase" style={{ color: v.accent }}>
                    {v.title}
                  </p>
                  <p className="mt-3 font-display text-xl font-semibold text-tnn-ink leading-snug">{v.question}</p>
                  <p className="mt-2 text-sm text-tnn-muted leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Who is it for */}
      <section className="bg-white py-16 lg:py-24" data-testid="book-audience">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Who Is the Book For?</Eyebrow>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-10">
            {AUDIENCES.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.05}>
                <h3 className="font-display text-lg font-bold text-tnn-ink leading-snug">{a.title}</h3>
                <p className="mt-2 text-sm text-tnn-muted leading-relaxed">{a.body}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <SampleChapter />

      {/* Why Tito wrote this book */}
      <section className="bg-tnn-soft py-16 lg:py-24" data-testid="book-author">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Written by Tito Ipinmoye</Eyebrow>
            <p className="mt-3 text-xs font-bold tracking-[0.18em] uppercase text-tnn-muted">
              Clinical dietitian · Product leader · Healthcare innovation executive
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <blockquote className="mt-8 font-display text-2xl sm:text-3xl text-tnn-ink leading-snug">
              "The first mistake I made building a healthcare product was thinking the product was the app."
            </blockquote>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-6 text-base text-tnn-muted leading-relaxed">
              Tito founded The Nutrition Network Africa in 2021 with Evi Uwede, with the organisation built around
              carrying clinical expertise beyond the clinic into products, ventures, education and systems. This
              book brings that journey into a practical framework for people building healthcare technology,
              particularly in the complex realities of African healthcare.
            </p>
            <Link
              to="/founders"
              data-testid="book-author-link"
              className="group mt-5 inline-flex items-center gap-2 text-sm font-semibold text-tnn-purple hover:text-tnn-deep transition-colors duration-200"
            >
              Read more about Tito
              <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Book information */}
      <section className="bg-white py-16 lg:py-24" data-testid="book-info">        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Book Information</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <div className="mt-8 bg-tnn-warm border border-tnn-ink/8 rounded-3xl p-8 lg:p-10">
              <h2 className="font-display text-xl sm:text-2xl font-bold text-tnn-ink leading-snug">
                Building Digital Health Products That Actually Work
              </h2>
              <dl className="mt-6 divide-y divide-tnn-ink/8">
                {BOOK_INFO.filter(([k]) => Date.now() < RELEASE_AT || k !== "Paperback preorder").map(([k, v]) => (
                  <div key={k} className="flex items-baseline justify-between gap-6 py-3.5">
                    <dt className="text-[11px] font-bold tracking-[0.18em] uppercase text-tnn-muted shrink-0">{k}</dt>
                    <dd className="text-sm font-semibold text-tnn-ink text-right">{v}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-5 text-xs text-tnn-muted leading-relaxed">
                Preorders are for the physical paperback only. The Kindle edition will be available at launch.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Waitlist */}
      <section id="book-waitlist" className="bg-tnn-deep py-16 lg:py-24 scroll-mt-24" data-testid="book-waitlist">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-start">
          <Reveal>
            <p className="text-xs font-bold tracking-[0.22em] uppercase text-tnn-lilac">Not ready to pre-order?</p>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-white leading-[1.15]">
              Join the waitlist.
            </h2>
            <p className="mt-4 text-sm sm:text-base text-white/65 leading-relaxed max-w-md">
              Get launch updates, behind-the-scenes content, book excerpts and a reminder when the book officially
              launches.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <WaitlistForm />
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="book-final-cta">
        <div className="max-w-3xl mx-auto px-6">
          <Reveal>
            <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
              Healthcare technology should do more than exist.
              <br />
              It should <span className="text-tnn-purple">actually work.</span>
            </h2>
            <p className="mt-4 text-xs font-bold tracking-[0.22em] uppercase text-tnn-purple">January 25, 2027</p>
          </Reveal>
          <Reveal delay={0.12} className="mt-8">
            <PreorderBlock testidPrefix="book-final" />
          </Reveal>
        </div>
      </section>
    </main>
  );
}
